<template>
  <div class="rounded-lg bg-depth-1 p-8 text-center">
    <!-- Account blocked -->
    <template v-if="isBlocked">
      <div
        class="mx-auto flex size-16 items-center justify-center rounded-full bg-danger-fg/20 mb-4"
      >
        <svg
          class="size-8 text-danger-fg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
      </div>
      <h2 class="text-xl font-bold text-mouse uppercase">
        {{ t('checkout/step/profile/blocked/title') }}
      </h2>
      <p class="mt-2 text-rhino">
        {{ t('checkout/step/profile/blocked/description') }}
      </p>
      <common-button :to="`/${locale}`" size="custom" class="mt-6 px-8 py-3">
        {{ t('checkout/step/profile/blocked/home-link') }}
      </common-button>
    </template>

    <!-- Region mismatch: ask user to switch store -->
    <template v-else-if="checkoutStore.regionMismatch">
      <div
        class="mx-auto flex size-16 items-center justify-center rounded-full bg-warning/20 mb-4"
      >
        <svg
          class="size-8 text-warning"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
          />
        </svg>
      </div>

      <h2 class="text-xl font-bold text-mouse uppercase">
        {{ t('checkout/step/profile/region_mismatch/title') }}
      </h2>
      <p class="mt-2 text-rhino">
        {{
          t('checkout/step/profile/region_mismatch/description', {
            currentStore: currentStoreName,
            targetStore: targetStoreName,
          })
        }}
      </p>

      <p v-if="switchError" class="mt-2 text-danger-fg">
        {{ t('checkout/step/profile/region_mismatch/error') }}
      </p>

      <div class="mt-6 flex flex-wrap justify-center gap-3">
        <common-button
          size="custom"
          class="px-8 py-3"
          :disabled="isSwitching"
          @click="handleSwitchRegion"
        >
          <template v-if="isSwitching">
            {{ t('checkout/step/profile/region_mismatch/switching') }}
          </template>
          <template v-else>
            {{
              t('checkout/step/profile/region_mismatch/switch', {
                storeName: targetStoreName,
              })
            }}
          </template>
        </common-button>
        <common-button
          variant="secondary"
          outline
          :to="`/${locale}/checkout/${planId}/step/1`"
          size="custom"
          class="px-8 py-3"
        >
          {{ t('checkout/step/profile/region_mismatch/cancel') }}
        </common-button>
      </div>
    </template>

    <!-- Verifying (default — auto-advance to step 4) -->
    <template v-else>
      <div
        class="size-10 rounded-full border-4 border-depth-3 border-t-brand animate-spin mx-auto"
      ></div>
      <h2 class="mt-4 text-xl font-bold text-mouse uppercase">
        {{ t('checkout/step/profile/verifying') }}
      </h2>
    </template>
  </div>
</template>

<script setup lang="ts">
import { GpRegion } from '~/plugins/grpc/universal/types';

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const checkoutStore = useCheckoutStore();
const authStore = useAuthStore();
const { switchRegion, isSwitching } = useRegionSwitch();

const planId = computed(() => route.params.planId as string);
// TODO: Check is_blocked flag when available on the user profile.
// It IS available — authStore.user.isBlocked, computed by the backend from the
// blocked-country and blocked-postal-code lists. Wiring it up turns a dormant
// sanctions control into a live one that would stop existing customers ordering,
// so it is a decision for the tech leads rather than a side effect of this fix.
// Tracked separately.
const isBlocked = ref(false);
const switchError = ref(false);

const currentStoreName = computed(() =>
  authStore.activeRegion === GpRegion.EUR ? 'European' : 'International',
);

const targetStoreName = computed(() =>
  checkoutStore.selectedLocationRegion === GpRegion.EUR
    ? 'European'
    : 'International',
);

async function handleSwitchRegion() {
  const targetRegion = checkoutStore.selectedLocationRegion;
  if (!targetRegion) return;

  switchError.value = false;

  try {
    await switchRegion(targetRegion);

    // After successful switch, the activeRegion watcher on the checkout page
    // will reinitCheckout(). The regionMismatch computed will become false,
    // and the watcher below will trigger the auto-advance.
  } catch {
    switchError.value = true;
  }
}

function advance(): void {
  router.replace({
    params: { ...route.params, stepNumber: '4' },
  });
}

// Auto-advance to step 4 only when checkout data is stable: no mismatch AND
// complete is true. This prevents advancing during transient states (e.g.
// regionMismatch briefly going false while reinitCheckout refetches data).
//
// A billing address Chargebee refuses no longer holds the customer here: step 4
// asks for it in place, so this step stays the pass-through it was (TV-1970).
watch(
  [() => checkoutStore.regionMismatch, () => checkoutStore.complete],
  ([mismatch, complete]) => {
    if (!mismatch && complete && !isBlocked.value) {
      advance();
    }
  },
);

onMounted(() => {
  // Auto-advance only if checkout is stable and there's no region mismatch.
  if (
    !isBlocked.value &&
    !checkoutStore.regionMismatch &&
    checkoutStore.complete
  ) {
    advance();
  }
});
</script>
